'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';

export type CliReplayData = {
  command: string;
  workingDirectory?: string;
};

export type CliTerminalTarget = {
  toolCallId?: string;
  ptyId?: string;
};

type CliReplayContextType = {
  sendToTerminal: (data: CliReplayData) => void;
  isAvailable: boolean;
  openTerminal?: (target: CliTerminalTarget) => void;
};

const CliReplayContext = createContext<CliReplayContextType | undefined>(
  undefined,
);

type CliReplayProviderProps = {
  children: ReactNode;
  onSendToTerminal?: (data: CliReplayData) => void;
  onOpenTerminal?: (target: CliTerminalTarget) => void;
};

export function CliReplayProvider({
  children,
  onSendToTerminal,
  onOpenTerminal,
}: CliReplayProviderProps) {
  const contextValue: CliReplayContextType = useMemo(
    () => ({
      sendToTerminal: (data: CliReplayData) => {
        if (onSendToTerminal) {
          onSendToTerminal(data);
        }
      },
      isAvailable: !!onSendToTerminal,
      openTerminal: onOpenTerminal,
    }),
    [onSendToTerminal, onOpenTerminal],
  );

  return (
    <CliReplayContext.Provider value={contextValue}>
      {children}
    </CliReplayContext.Provider>
  );
}

export function useCliReplayContext(): CliReplayContextType | undefined {
  return useContext(CliReplayContext);
}

export function useSendToTerminal():
  | ((data: CliReplayData) => void)
  | undefined {
  const context = useContext(CliReplayContext);

  if (!context?.isAvailable) return undefined;

  return context.sendToTerminal;
}

export function useOpenInTerminal():
  | ((target: CliTerminalTarget) => void)
  | undefined {
  return useContext(CliReplayContext)?.openTerminal;
}
